import React, { useEffect, useMemo, useState } from "react"
import { HorairesOuverture, WorkingHour } from "./type"
import { WorkingHoursTable } from "./WorkingHoursTable"
import { ModalWorkingHours } from "./ModalWorkingHours"
import { ModalHolidaysDate } from "./ModalHolidaysDate"
import ModalDeleteWorkingHours from "./ModalDeleteWorkingHours"
import { SelectOption } from "../../types/SelectOption"
import { orderBy } from "lodash"
import BackOfficeAPIClient from "../../clients/BackofficeAPIClient"
import { Timezone } from "../FuseauHoraire/type"

declare const window: any

interface Dealership
{
    IDSite: number
    Descr: string
}

export default function List() {
    const backofficeApiClient = new BackOfficeAPIClient()

    const [ workingHours, setWorkingHours ] = useState<WorkingHour[]>([])
    const [ workingHoursToEdit, setWorkingHoursToEdit ] = useState<WorkingHour[] | null>(null)
    const [ workingHoursToDuplicate, setWorkingHoursToDuplicate ] = useState<WorkingHour[] | null>(null)
    const [ workingHoursIdToDelete, setWorkingHoursIdToDelete ] = useState<number | null>()
    const [ codePays, setCodePays ] = useState<string>()
    const [ isFetchingCodePays, setIsFetchingCodePays ] = useState(true)
    const [ dealerships, setDealerships ] = useState<SelectOption[]>([])
    const [ isGlobalConfig, setIsGlobalConfig ] = useState<boolean | undefined>(undefined)
    const [ timezones, setTimezones ] = useState<Timezone[]>()

    const fetchWorkingHours = async (): Promise<void> => {
        try {
            const json = await backofficeApiClient.get<HorairesOuverture[]>('/workingHours')

            setWorkingHours(json.map((ho) => {
                const horaires = JSON.parse(ho.Horaires)
                return {
                    id: ho.IDWorkingHours,
                    department: ho.Departement,
                    dealershipIds: ho.IDSite ? [ ho.IDSite ] : null,
                    schedules: horaires,
                } as WorkingHour
            }))
        } catch (err) {
            console.log("Error", err)
        }
    }

    const fetchTimezoneConfigurations = async (): Promise<void> => {
        const configurations = await backofficeApiClient.get<Timezone[]>('/fuseauHoraire')
        setTimezones(configurations)
    }

    const fetchGpParametres = async (): Promise<void> => {
        setIsFetchingCodePays(true)
        const json = await backofficeApiClient.get<{ GRP_CodePays: string }>('/GpParametres')
        setCodePays(json.GRP_CodePays)
        setIsFetchingCodePays(false)
    }

    const fetchDealerships = async (): Promise<void> => {
        try {
            const dealershipsRetrieved = orderBy(await backofficeApiClient.get<Dealership[]>("/sites"), "Descr")
            const formattedDealerships = dealershipsRetrieved.map((dealership) => {
                return {
                    label: dealership.Descr,
                    value: dealership.IDSite,
                } as SelectOption
            })
            setDealerships(formattedDealerships)
        } catch (err) {
            console.error("Erreur lors de la récupération de la configuration des sites", err)
        }
    }

    const openWorkingHoursModal = (workingHours: WorkingHour | null = null): void => {
        setWorkingHoursToEdit(workingHours ? [ workingHours ] : null)
        setWorkingHoursToDuplicate(null)
        setIsGlobalConfig(undefined) // Auto-détecte depuis workingHours
        window.$('#modalWorkingHours').modal('show')
    }

    const openGlobalWorkingHoursModal = (): void => {
        setWorkingHoursToEdit(null)
        setWorkingHoursToDuplicate(null)
        setIsGlobalConfig(true)
        window.$('#modalWorkingHours').modal('show')
    }

    const openSiteWorkingHoursModal = (): void => {
        setWorkingHoursToEdit(null)
        setWorkingHoursToDuplicate(null)
        setIsGlobalConfig(false)
        window.$('#modalWorkingHours').modal('show')
    }

    const openDuplicateModal = (workingHour: WorkingHour): void => {
        setWorkingHoursToDuplicate([ workingHour ])
        setWorkingHoursToEdit(null)
        setIsGlobalConfig(undefined) // Auto-détecte
        window.$('#modalWorkingHours').modal('show')
    }

    const openHolidaysModal = (): void => {
        window.$('#modalHolidaysDates').modal('show')
    }

    const openDeleteModal = (workingHoursId: number): void => {
        setWorkingHoursIdToDelete(workingHoursId)
        window.$('#modalDeleteWorkingHours').modal('show')
    }

    const handleDelete = async (): Promise<void> => {
        try {
            await backofficeApiClient.delete(`/workingHours/${workingHoursIdToDelete}`, null)
            window.$('#modalDeleteWorkingHours').modal('hide')
            await fetchWorkingHours()
        } catch (e) {
            console.error("Error occured on deleting config", e)
        }
    }

    const globalWorkingHours = useMemo(() => {
        return workingHours.filter((wh) => !wh.dealershipIds)
    }, [ workingHours ])

    const byDealershipWorkingHours = useMemo(() => {
        return workingHours.filter((wh) => !!wh.dealershipIds)
    }, [ workingHours ])

    useEffect(() => {
        fetchWorkingHours()
        fetchTimezoneConfigurations()
        fetchGpParametres()
        fetchDealerships()
    }, [])

    return (
        <>
            <div className="tw-flex tw-flex-col tw-gap-5 tw-px-6">
                <div className="tw-flex tw-flex-row tw-justify-between">
                    <span className="tw-text-2xl tw-font-semibold">Paramétrage Horaires d'ouverture</span>
                    <button className="btn btn-primary tw-text-sm"
                            disabled={isFetchingCodePays}
                            onClick={() => openHolidaysModal()}
                    >
                        Définir les jours fériés
                    </button>
                </div>
                <div className="tw-flex tw-flex-col tw-gap-3">
                    <div className="tw-flex tw-flex-row tw-justify-between tw-items-center">
                        <span className="tw-text-xl">Paramétrage Global</span>
                        <button className="btn btn-primary tw-text-sm"
                                onClick={() => openGlobalWorkingHoursModal()}
                        >
                            <i className="fa fa-plus tw-mr-2"></i>
                            Ajouter une config globale
                        </button>
                    </div>
                    <div className="tw-flex tw-flex-col tw-gap-4">
                        <div>
                            <WorkingHoursTable
                                onDelete={openDeleteModal}
                                timezones={timezones?.filter((timezone) => timezone.siteId === null)}
                                onDuplicate={openDuplicateModal}
                                workingHours={globalWorkingHours}
                                openEditModal={openWorkingHoursModal}
                                dealerships={dealerships}
                            />
                        </div>
                    </div>
                </div>

                <div className="tw-flex tw-flex-col tw-gap-3">
                    <div className="tw-flex tw-flex-row tw-justify-between tw-items-center">
                        <span className="tw-text-xl">Paramétrage par Site</span>
                        <button className="btn btn-primary tw-text-sm"
                                onClick={() => openSiteWorkingHoursModal()}
                        >
                            <i className="fa fa-plus tw-mr-2"></i>
                            Ajouter une config par site
                        </button>
                    </div>
                    <div className="tw-flex tw-flex-col tw-gap-4">
                        <div>
                            <WorkingHoursTable
                                onDelete={openDeleteModal}
                                onDuplicate={openDuplicateModal}
                                timezones={timezones}
                                workingHours={byDealershipWorkingHours}
                                isDealershipSettings={true}
                                openEditModal={openWorkingHoursModal}
                                dealerships={dealerships}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <ModalWorkingHours
                refetchData={fetchWorkingHours}
                workingHours={workingHoursToDuplicate || workingHoursToEdit}
                mode={workingHoursToDuplicate ? "duplicate" : undefined}
                allWorkingHours={workingHours}
                dealerships={dealerships}
                isGlobalConfig={isGlobalConfig}
            />
            {!isFetchingCodePays &&
                <ModalHolidaysDate
                    codePays={codePays}
                    dealerships={dealerships}
                />
            }
            <ModalDeleteWorkingHours handleConfirm={handleDelete}/>
        </>
    )
}
